/**
 * BlogPost view (Story 30.10) — renders a single MDX post with SEO meta,
 * BlogPosting JSON-LD, TOC, reading time, visible dates, author bio, and
 * related articles.
 */
import { MDXProvider } from "@mdx-js/react";
import { Link } from "@tanstack/react-router";
import { useSEO } from "../hooks/useSEO";
import { JsonLd } from "../seo/JsonLd";
import { articleSchema, faqPageSchema } from "../seo/schemas";
import { SITE_ORIGIN } from "../seo/seoConfig";
import { mdxComponents } from "./mdxComponents";
import { getRelatedPosts } from "./posts";
import { getAuthorByName } from "../data/authors";
import { Breadcrumb } from "../components/shared/Breadcrumb";
import type { BlogPost } from "./types";

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

const CATEGORY_LABELS: Record<string, string> = {
  comparison: "Comparison",
  listicle: "Roundup",
  pillar: "Guide",
  guide: "Guide",
};

function categoryLabel(category: string): string {
  if (!category) return "";
  return CATEGORY_LABELS[category] ?? category.charAt(0).toUpperCase() + category.slice(1);
}

export function BlogPostView({ post }: { post: BlogPost }) {
  const { frontmatter: fm, slug, Component, readingTimeMinutes, toc } = post;
  const url = `${SITE_ORIGIN}/blog/${slug}`;
  const related = getRelatedPosts(post);

  useSEO({
    title: `${fm.title} — ProvenLeap`.length <= 60 ? `${fm.title} — ProvenLeap` : fm.title,
    description: fm.description,
    path: `/blog/${slug}`,
    ogType: "article",
    // Drafts are excluded from the sitemap/index/RSS/prerender, but a shared
    // draft URL is otherwise index:follow — noindex it so a leaked link can't
    // be indexed before the post is published.
    ...(fm.draft ? { robots: "noindex, nofollow" } : {}),
    ...(fm.image ? { image: fm.image.startsWith("http") ? fm.image : `${SITE_ORIGIN}${fm.image}` } : {}),
  });

  return (
    <article className="mx-auto max-w-3xl px-4 py-10 sm:py-16">
      <JsonLd
        data={articleSchema({
          headline: fm.title,
          description: fm.description,
          url,
          datePublished: fm.publishedAt,
          dateModified: fm.updatedAt,
          authorName: fm.author,
          ...(fm.image ? { image: fm.image.startsWith("http") ? fm.image : `${SITE_ORIGIN}${fm.image}` } : {}),
        })}
      />

      <div className="mb-6">
        <Breadcrumb
          items={[
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: fm.title, url: `/blog/${slug}` },
          ]}
        />
      </div>

      <header className="mb-8">
        {fm.category && (
          <span className="inline-flex items-center rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand">
            {categoryLabel(fm.category)}
          </span>
        )}
        <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-[2.6rem]">
          {fm.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="font-medium text-foreground/80">{fm.author}</span>
          <span aria-hidden>·</span>
          <span>
            Published <time dateTime={fm.publishedAt}>{formatDate(fm.publishedAt)}</time>
          </span>
          {fm.updatedAt !== fm.publishedAt && (
            <>
              <span aria-hidden>·</span>
              <span>
                Updated <time dateTime={fm.updatedAt}>{formatDate(fm.updatedAt)}</time>
              </span>
            </>
          )}
          <span aria-hidden>·</span>
          <span>{readingTimeMinutes} min read</span>
        </div>
        {fm.tldr && (
          <div className="mt-6 rounded-xl border-[0.5px] border-input bg-muted/40 p-5">
            <p className="text-[12px] font-semibold uppercase tracking-widest text-muted-foreground">TL;DR</p>
            {/* Split the summary into sentences and space them out so the TL;DR
                reads as scannable lines, not a dense wall of text. */}
            <div className="mt-2.5 space-y-2.5">
              {fm.tldr
                .split(/(?<=\.)\s+(?=[A-Z])/)
                .map((sentence) => sentence.trim())
                .filter(Boolean)
                .map((sentence, i) => (
                  <p key={i} className="text-[15.5px] leading-relaxed text-foreground/85">
                    {sentence}
                  </p>
                ))}
            </div>
          </div>
        )}
      </header>

      {toc.length >= 3 && (
        <nav className="mb-8 rounded-xl border-[0.5px] border-input p-4" aria-label="Table of contents">
          <p className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
            On this page
          </p>
          <ul className="space-y-1 text-sm">
            {toc.map((t) => (
              <li key={t.id} className={t.depth === 3 ? "ml-4" : ""}>
                <a href={`#${t.id}`} className="text-brand hover:underline">
                  {t.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="prose-provenleap">
        <MDXProvider components={mdxComponents}>
          <Component />
        </MDXProvider>
      </div>

      {/* Optional FAQ (comparison/listicle/pillar posts) — visible + FAQPage
          JSON-LD from the SAME frontmatter data (Google parity). */}
      {fm.faq && fm.faq.length > 0 && (
        <section className="mt-12" aria-labelledby="post-faq-heading">
          <JsonLd data={faqPageSchema(fm.faq)} />
          <h2 id="post-faq-heading" className="mb-4 text-2xl font-semibold tracking-tight">
            Frequently asked questions
          </h2>
          <dl className="space-y-5">
            {fm.faq.map((qa) => (
              <div key={qa.question}>
                <dt className="font-semibold text-foreground">{qa.question}</dt>
                <dd className="mt-1 text-[16px] leading-relaxed text-foreground/85">{qa.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* Author bio — sourced from authors.ts (Story 30.15) when the post's
          author matches a known author, falling back to the frontmatter. */}
      <footer className="mt-12 border-t-[0.5px] border-input pt-6">
        {(() => {
          const author = getAuthorByName(fm.author);
          const known = author.name === fm.author;
          return (
            <>
              <p className="text-sm font-semibold text-foreground">{fm.author}</p>
              <p className="text-sm text-muted-foreground">{known ? author.title : fm.authorTitle}</p>
              {known && <p className="mt-1 text-sm text-foreground/80">{author.bio}</p>}
              <Link to="/about" className="mt-1 inline-block text-sm text-brand hover:underline">
                About the author
              </Link>
            </>
          );
        })()}
      </footer>

      {related.length > 0 && (
        <section className="mt-12" aria-labelledby="related-heading">
          <h2 id="related-heading" className="mb-4 text-xl font-semibold text-foreground">
            Related articles
          </h2>
          <ul className="space-y-3">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to="/blog/$slug" params={{ slug: r.slug }} className="text-brand hover:underline">
                  {r.frontmatter.title}
                </Link>
                <p className="text-sm text-muted-foreground">{r.frontmatter.description}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
